import { Button } from '@design-system/components/button'
import { Text } from '@design-system/components/Text'
import Link from '@/routing/Link'
import { cn } from '@/lib/utils'
import type { MemberUsage } from '@/lib/api'
import { getUserInitials } from '@/lib/utils/avatar'
import { UserAvatar } from '@/components/ui/UserAvatar'
import { OverageInfoTooltip } from './OverageInfoTooltip'

interface TeamUsageSectionProps {
  members: MemberUsage[]
  loading?: boolean
  /** Where the "Manage team" button should navigate. */
  manageTeamHref: string
  /** Per-credit overage rate in cents (current org plan/interval). */
  overageRateCents: number
}

export function TeamUsageSection(props: TeamUsageSectionProps) {
  const { members, loading, manageTeamHref, overageRateCents } = props

  return (
    <div className="flex flex-col gap-4" data-testid="billing-team-usage">
      <div className="flex items-center justify-between">
        <Text size="sm" weight="medium">
          Team usage
        </Text>
        <Link href={manageTeamHref}>
          <Button
            variant="outline"
            size="sm"
            data-testid="billing-manage-team"
            className="h-9 gap-1.5 rounded-md border-cta px-3 text-sm font-medium text-cta hover:bg-cta/5 dark:border-cta dark:text-cta dark:hover:bg-cta/10"
          >
            Manage team
          </Button>
        </Link>
      </div>

      <div className="overflow-hidden">
        <table className="w-full border-separate border-spacing-0 text-left text-sm">
          <thead>
            <tr>
              <th scope="col" className="rounded-l-xl bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground">
                Member
              </th>
              <th scope="col" className="bg-muted/50 px-5 py-3 text-xs font-normal text-muted-foreground">
                Credits used
              </th>
              <th scope="col" className="w-[140px] rounded-r-xl bg-muted/50 px-5 py-3 text-right text-xs font-normal text-muted-foreground">
                Part
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={3} className="px-5 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && members.length === 0 && (
              <tr>
                <td colSpan={3} className="px-5 py-8 text-center text-sm text-muted-foreground">
                  No team members yet.
                </td>
              </tr>
            )}
            {members.map((m, idx) => {
              const pct = m.creditLimit > 0 ? Math.round((m.used / m.creditLimit) * 100) : 0
              const isOver = m.isPoolBilling ? false : pct > 100
              const fillPct = Math.min(pct, 100)
              const role = m.orgRole
                ? m.orgRole[0]!.toUpperCase() + m.orgRole.slice(1)
                : null
              const initials = getUserInitials(m.name, m.email)
              const isLastRow = idx === members.length - 1
              const cellBorder = isLastRow ? '' : 'border-b border-border/40 dark:border-border'

              return (
                <tr key={m.userId} className="align-middle">
                  <td className={`px-5 py-3.5 ${cellBorder}`}>
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        initials={initials}
                        name={m.name || m.email}
                        size="xs"
                      />
                      <div className="min-w-0">
                        <div className="truncate text-sm">
                          <span className="font-medium text-foreground">
                            {m.name || m.email}
                          </span>
                          {role ? (
                            <span className="ml-1 text-muted-foreground">({role})</span>
                          ) : null}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {m.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={`px-5 py-3.5 ${cellBorder}`}>
                    <div className="flex items-center gap-3">
                      <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            isOver ? 'bg-destructive' : 'bg-chart-positive',
                          )}
                          style={{ width: `${fillPct}%` }}
                        />
                        {isOver ? (
                          <span
                            aria-hidden
                            className="absolute right-0 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-destructive ring-2 ring-card-inset"
                          />
                        ) : null}
                      </div>
                      <span
                        className={cn(
                          'shrink-0 text-xs tabular-nums',
                          isOver ? 'font-medium text-destructive' : 'text-muted-foreground',
                        )}
                      >
                        {m.isPoolBilling
                          ? `${m.used.toLocaleString()} credits`
                          : `${m.used.toLocaleString()}/${m.creditLimit.toLocaleString()}`}
                      </span>
                    </div>
                  </td>
                  <td className={`px-5 py-3.5 text-right ${cellBorder}`}>
                    <div className="inline-flex items-center justify-end gap-1">
                      <span
                        className={cn(
                          'tabular-nums',
                          isOver ? 'font-medium text-destructive' : 'text-foreground',
                        )}
                        data-testid={`billing-team-usage-pct-${m.userId}`}
                      >
                        {pct}%
                      </span>
                      {isOver ? (
                        <OverageInfoTooltip
                          overageCredits={m.overageUsed}
                          rateCents={overageRateCents}
                          side="left"
                          label={`Overage details for ${m.name || m.email}`}
                          triggerTestId={`billing-team-usage-overage-info-${m.userId}`}
                        />
                      ) : null}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
